import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../api/axios";
import { toast } from "react-toastify";
import Select from "react-select";
import { Button, Col, Form, Row, Spinner, Card, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import Header from "../../layouts/Header";

const AddPurchase = () => {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);
  const navigate = useNavigate();
  const { access } = useSelector((state) => state.login);
  const accessToken = localStorage.getItem("authToken");

  // Refs for keyboard navigation
  const vendorRef = useRef(null);
  const idNumberRef = useRef(null);
  const additionalChargesRef = useRef(null);
  const fileInputRef = useRef(null);
  const submitButtonRef = useRef(null);
  const itemRefs = useRef([]);

  // State for form data - NEW ITEMS WILL BE ADDED AT THE BEGINNING
  const [inputData, setInputData] = useState({
    vendor: "",
    idNumber: "",
    additionalCharges: "",
    procurementItems: [
      {
        material: "",
        quantity: "",
        rate: "",
        taxPercent: "",
        unit: "",
        actualRate: "",
        taxable: 0,
        taxAmount: 0,
        grandTotal: 0,
      },
    ],
  });

  const [validationErrors, setValidationErrors] = useState({
    vendor: "",
    idNumber: "",
    additionalCharges: "",
    invoiceFile: "",
    procurementItems: [
      {
        material: "",
        quantity: "",
        rate: "",
      },
    ],
  });

  const [isPurchaseLoading, setIsPurchaseLoading] = useState(false);
  const [isPurchaseDrafting, setIsPurchaseDrafting] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [draftedProcurements, setDraftedProcurements] = useState([]);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [rawMaterialList, setRawMaterialList] = useState([]);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [focusedItemIndex, setFocusedItemIndex] = useState(0);

  const [showAddModal, setShowAddModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    mainCategory: "",
    subCategory: "",
    name: "",
    reOrderLevel: "",
    description: "",
    unit: "",
    hsn: "",
    taxPercent: "",
    purchaseUnit: "",
  });
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [filteredSubCategories, setFilteredSubCategories] = useState([]);

  // Format options for react-select
  const mainCategoryOptions = mainCategories?.map(cat => ({
    value: cat._id,
    label: cat.name
  }));

  const subCategoryOptions = filteredSubCategories?.map(cat => ({
    value: cat._id,
    label: cat.name
  }));

  // Fetch initial data
  useEffect(() => {
    getVendors();
    getRawMaterials();
    // Focus on first field
    setTimeout(() => {
      if (vendorRef.current) {
        vendorRef.current.focus();
      }
    }, 500);
  }, []);

  useEffect(() => {
    getMainCategories();
    getSubCategories();
  }, [showAddModal]);

  // Keyboard navigation handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + N for new item (adds at top)
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        handleAddItem();
      }
      // Ctrl/Cmd + S for submit
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (!purchaseBtnDisable() && !isPurchaseLoading) {
          handlePurchaseSubmit(e);
        }
      }
      // Alt + N for new material modal
      if (e.altKey && e.key === 'n') {
        e.preventDefault();
        handleOpenNewMaterialModal();
      }
      // F2 for focusing on first item
      if (e.key === 'F2') {
        e.preventDefault();
        focusOnItem(0);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [inputData.procurementItems.length, isPurchaseLoading]);

  // API Calls
  const getVendors = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get("vendor", { headers });
      if (response?.data?.status === true) {
        setVendors(response?.data?.data?.vendors);
      }
    } catch (error) {
      handleApiError(error, "Failed to fetch vendors");
    }
  };

  const getRawMaterials = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get("raw-material", { headers });
      if (response?.data?.status === true) {
        setRawMaterialList(response?.data?.data?.rawMaterials);
      }
    } catch (error) {
      handleApiError(error, "Failed to fetch raw materials");
    }
  };

  const getMainCategories = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get("masters/rm-main-category/get", { headers });
      if (response?.data?.status === true) {
        setMainCategories(response.data.data.categories);
      }
    } catch (error) {
      handleApiError(error, "Failed to fetch main categories");
    }
  };

  const getSubCategories = async (mainCategoryId = null) => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get("masters/rm-sub-category/get", { headers });

      if (response?.data?.status === true) {
        const allSubCategories = response.data.data.categories;
        setSubCategories(allSubCategories);

        if (mainCategoryId) {
          const filtered = allSubCategories.filter(sub => sub.mainCategory === mainCategoryId);
          setFilteredSubCategories(filtered);
        }
      }
    } catch (error) {
      handleApiError(error, "Failed to fetch sub categories");
    }
  };

  const getSingleRawMaterialDetail = async (materialId, index) => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get(`raw-material?_id=${materialId}`, { headers });

      if (response?.data?.status === true) {
        const raw = response?.data?.data?.rawMaterials;
        const taxPercent = raw?.taxPercent || 0;
        const unit = raw?.unit || "";

        setInputData((prevData) => {
          const updatedItems = [...prevData.procurementItems];
          updatedItems[index] = {
            ...updatedItems[index],
            taxPercent,
            unit,
          };
          return { ...prevData, procurementItems: updatedItems };
        });

        // Auto-focus on quantity after material selection
        setTimeout(() => focusOnItem(index, 'quantity'), 100);
      }
    } catch (error) {
      console.error("Error fetching raw material details:", error);
    }
  };

  const addMaterial = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const headers = { Authorization: `Bearer ${accessToken}` };
      const data = { ...formData };

      if (!data.reOrderLevel) delete data.reOrderLevel;
      if (!data.purchaseUnit) delete data.purchaseUnit;

      const response = await axios.post("raw-material", data, { headers });

      if (response?.data?.status === true) {
        toast.success(response.data.data.message);
        await getRawMaterials();
        resetForm();
        setShowAddModal(false);
        // Focus back on the first material select after modal closes
        setTimeout(() => focusOnItem(0, 'material'), 500);
      }
    } catch (error) {
      handleApiError(error, "Failed to add material");
    } finally {
      setIsLoading(false);
    }
  };

  // Helper functions
  const handleApiError = (error, defaultMessage) => {
    let errorMessage = defaultMessage;

    if (error?.response) {
      if (error.response.status === 400) {
        errorMessage = error.response.data.message || error.response.data.title;
      } else if (error.response.status === 404) {
        errorMessage = error.response.data.message || "Resource not found";
      } else if (error.response.status === 406) {
        errorMessage = error.response.data.error?.message || error.response.data.error;
      } else if (error.response.status === 412) {
        errorMessage = error.response.data.data?.message || error.response.data.error;
      } else if (error.response.status === 500) {
        errorMessage = error.response.data.error || "Server error";
      }
    }

    toast.error(errorMessage);
  };

  const calculateTaxAmount = (rate, quantity, taxPercent, unit, actualRate) => {
    let taxable = 0;

    if (unit === "GRAMS" || unit === "ML") {
      taxable = actualRate * (quantity / 1000);
    } else {
      taxable = rate * quantity;
    }

    taxable = Number(taxable.toFixed(2));
    const taxAmount = Number(((taxable * taxPercent) / 100).toFixed(2));
    const grandTotal = Number((taxable + taxAmount).toFixed(2));

    return { taxable, taxAmount, grandTotal };
  };

  // Focus management
  const focusOnItem = (index, field = 'material') => {
    setFocusedItemIndex(index);
    const elementId = `${field}-${index}`;
    const element = document.getElementById(elementId);

    if (element) {
      element.focus();
      if (element.tagName === 'INPUT') {
        element.select();
      }
    }
  };

  const handleKeyDown = (e, index, field) => {
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        // Navigate through fields
        const fieldOrder = ['material', 'quantity', 'actualRate', 'rate'];
        const currentIndex = fieldOrder.indexOf(field);

        if (currentIndex < fieldOrder.length - 1) {
          // Move to next field in same item
          focusOnItem(index, fieldOrder[currentIndex + 1]);
        } else if (index === 0) {
          // If last field of first item, add new item at top
          if (validateCurrentItem(index)) {
            handleAddItem();
          }
        } else {
          // Move to first field of previous item (above)
          focusOnItem(index - 1, 'material');
        }
        break;

      case 'Tab':
        if (e.shiftKey) {
          // Handle shift+tab for backward navigation
          const fieldOrder = ['material', 'quantity', 'actualRate', 'rate'];
          const currentIndex = fieldOrder.indexOf(field);

          if (currentIndex > 0) {
            e.preventDefault();
            focusOnItem(index, fieldOrder[currentIndex - 1]);
          } else if (index < inputData.procurementItems.length - 1) {
            e.preventDefault();
            focusOnItem(index + 1, 'rate');
          }
        }
        break;

      case 'Delete':
        if (e.ctrlKey && inputData.procurementItems.length > 1) {
          e.preventDefault();
          handleRemoveItem(index);
        }
        break;

      case 'ArrowUp':
      case 'ArrowDown':
        if (e.altKey) {
          e.preventDefault();
          const newIndex = e.key === 'ArrowUp'
            ? Math.max(0, index - 1)
            : Math.min(inputData.procurementItems.length - 1, index + 1);
          focusOnItem(newIndex, field);
        }
        break;

      default:
        break;
    }
  };

  const validateCurrentItem = (index) => {
    const item = inputData.procurementItems[index];
    return item.material && item.quantity && item.actualRate;
  };

  // Form handlers
  const handlePurchaseChange = (e, index) => {
    const { name, value } = e.target;

    if (name === "idNumber") {
      setValidationErrors((prev) => ({ ...prev, idNumber: "" }));
      setInputData((prev) => ({ ...prev, idNumber: value }));
      return;
    }

    setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    setValidationErrors((prev) => ({ ...prev, procurementItems: [] }));

    if (["material", "quantity", "rate"].includes(name)) {
      if (name === "material" && value) {
        getSingleRawMaterialDetail(value, index);
      }

      setInputData((prev) => {
        const items = [...prev.procurementItems];
        const item = { ...items[index] };

        if (name === "material") {
          items[index] = {
            ...item,
            material: value,
            quantity: "",
            rate: "",
            actualRate: "",
            taxable: 0,
            taxAmount: 0,
            grandTotal: 0,
          };
          return { ...prev, procurementItems: items };
        }

        item[name] = parseFloat(value) || 0;

        const rate = Number(item.rate) || 0;
        const quantity = Number(item.quantity) || 0;
        const taxPercent = Number(item.taxPercent) || 0;

        const { taxable, taxAmount, grandTotal } = calculateTaxAmount(
          rate,
          quantity,
          taxPercent,
          item.unit,
          item.actualRate
        );

        items[index] = {
          ...item,
          taxable: Number(taxable.toFixed(2)),
          taxAmount: Number(taxAmount.toFixed(2)),
          grandTotal: Number(grandTotal.toFixed(2)),
        };

        return { ...prev, procurementItems: items };
      });

      return;
    }

    setInputData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRateChange = (e, index) => {
    const actualRate = parseFloat(e.target.value) || 0;

    setInputData((prev) => {
      const items = [...prev.procurementItems];
      const item = { ...items[index] };

      const quantity = parseFloat(item.quantity) || 0;
      const unit = item.unit;

      let rate = actualRate;

      if ((unit === "GRAMS" || unit === "ML") && actualRate > 0 && quantity > 0) {
        rate = actualRate / quantity;
      }

      rate = Number(rate.toFixed(5));

      const { taxable, taxAmount, grandTotal } = calculateTaxAmount(
        rate,
        quantity,
        item.taxPercent,
        unit,
        actualRate
      );

      items[index] = {
        ...item,
        actualRate,
        rate,
        taxable,
        taxAmount,
        grandTotal,
      };

      return { ...prev, procurementItems: items };
    });
  };

  const handlePurchaseFileChange = (e) => {
    setValidationErrors((prevErrors) => ({ ...prevErrors, invoiceFile: "" }));
    const selectedFile = e.target.files[0];
    setInvoiceFile(selectedFile);

    // Focus on first item after file selection
    setTimeout(() => focusOnItem(0, 'material'), 100);
  };

  // MODIFIED: Add new item at the BEGINNING (top) of the list
  const handleAddItem = () => {
    // Validate only if there are existing items with data
    // if (inputData.procurementItems.length > 0) {
    //   const firstItem = inputData.procurementItems[0];
    //   if (firstItem.material || firstItem.quantity || firstItem.actualRate) {
    //     const procurementItemErrors = inputData.procurementItems.map(validateProcurementItem);
    //     if (procurementItemErrors.some((errors) => Object.keys(errors).length > 0)) {
    //       setValidationErrors((prevErrors) => ({
    //         ...prevErrors,
    //         procurementItems: procurementItemErrors,
    //       }));

    //       // Focus on first item with error
    //       const errorIndex = procurementItemErrors.findIndex(errors => Object.keys(errors).length > 0);
    //       if (errorIndex !== -1) {
    //         focusOnItem(errorIndex, 'material');
    //       }
    //       return;
    //     }
    //   }
    // }

    // Create new item
    const newItem = {
      material: "",
      quantity: "",
      rate: "",
      taxPercent: "",
      unit: "",
      actualRate: "",
      taxable: 0,
      taxAmount: 0,
      grandTotal: 0,
    };

    // Add new item at the BEGINNING of the array
    setInputData((prevData) => ({
      ...prevData,
      procurementItems: [newItem, ...prevData.procurementItems],
    }));

    // Focus on new item's material field (which is now at index 0)
    setTimeout(() => focusOnItem(0, 'material'), 100);
  };

  // MODIFIED: Handle remove item with proper index
  const handleRemoveItem = (index) => {
    if (inputData.procurementItems.length <= 1) {
      toast.warning("At least one item is required");
      return;
    }

    setInputData((prevData) => {
      const updatedItems = [...prevData.procurementItems];
      updatedItems.splice(index, 1);
      return {
        ...prevData,
        procurementItems: updatedItems,
      };
    });

    // Focus on appropriate item after removal
    setTimeout(() => {
      if (index === 0 && inputData.procurementItems.length > 1) {
        // If removed the top item, focus on the new top item
        focusOnItem(0, 'material');
      } else {
        // Focus on the item above the removed one
        focusOnItem(Math.max(0, index - 1), 'material');
      }
    }, 100);
  };

  const validateProcurementItem = (item) => {
    const errors = {};

    if (!item.material || !item.material.trim()) {
      errors.material = "Material is required.";
    }

    if (item.quantity === null || item.quantity === undefined || Number(item.quantity) <= 0) {
      errors.quantity = "Quantity must be greater than 0.";
    }

    if (item.rate === null || item.rate === undefined || Number(item.rate) <= 0) {
      errors.rate = "Rate must be greater than 0.";
    }

    return errors;
  };

  const purchaseBtnDisable = () => {
    if (!inputData.vendor || !inputData.idNumber || !inputData.additionalCharges) {
      return true;
    }

    // Check if ANY item has incomplete data
    for (let i = 0; i < inputData.procurementItems.length; i++) {
      const item = inputData.procurementItems[i];
      if (item.material || item.quantity || item.actualRate) {
        // Only validate if the item has some data
        if (!item.material || !item.quantity || !item.actualRate) {
          return true;
        }
      }
    }

    return false;
  };

  const handlePurchaseSubmit = async (e) => {
    e.preventDefault();

    // Filter out empty items before submission
    const nonEmptyItems = inputData.procurementItems.filter(item =>
      item.material && item.quantity && item.actualRate
    );

    if (nonEmptyItems.length === 0) {
      toast.warning("Please add at least one procurement item");
      return;
    }

    // Validation
    if (!inputData.vendor) {
      setValidationErrors((prevErrors) => ({ ...prevErrors, vendor: "Vendor is required." }));
      vendorRef.current?.focus();
      return;
    }

    if (!inputData.idNumber) {
      setValidationErrors((prevErrors) => ({ ...prevErrors, idNumber: "ID Number is required." }));
      idNumberRef.current?.focus();
      return;
    }

    if (!inputData.additionalCharges) {
      setValidationErrors((prevErrors) => ({ ...prevErrors, additionalCharges: "Additional Charges is required." }));
      additionalChargesRef.current?.focus();
      return;
    }

    const procurementItemErrors = nonEmptyItems.map(validateProcurementItem);
    const hasErrors = procurementItemErrors.some((errors) => Object.keys(errors).length > 0);

    if (hasErrors) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        procurementItems: procurementItemErrors,
      }));

      // Focus on first item with error
      const errorIndex = procurementItemErrors.findIndex(errors => Object.keys(errors).length > 0);
      if (errorIndex !== -1) {
        focusOnItem(errorIndex, 'material');
      }
      return;
    }

    setIsPurchaseLoading(true);

    try {
      const formData = new FormData();
      formData.append("procurement[vendor]", inputData.vendor);
      formData.append("procurement[idNumber]", inputData.idNumber);
      formData.append("procurement[additionalCharges]", inputData.additionalCharges);
      formData.append("procurement.scannedInvoice", invoiceFile);

      // Submit only non-empty items
      nonEmptyItems.forEach((item, index) => {
        formData.append(`procurementItems[${index}][material]`, item.material);
        formData.append(`procurementItems[${index}][quantity]`, item.quantity);
        formData.append(`procurementItems[${index}][rate]`, item.rate);
      });

      const headers = {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${accessToken}`,
      };

      const response = await axios.post("procurement", formData, { headers });

      if (response?.data?.status === true) {
        toast.success(response?.data?.data?.message, {
          position: "top-right",
          autoClose: 3000,
          theme: "colored",
        });
        navigate("/purchase/purchase-list");
      }
    } catch (error) {
      if (error?.response) {
        let errorMessage = "";
        if (error.response?.status === 406) {
          errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
        } else if (error.response?.status === 412) {
          errorMessage = error?.response?.data?.data?.message;
        } else if (error.response?.status === 500) {
          errorMessage = error?.response?.data?.error;
        }
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } finally {
      setIsPurchaseLoading(false);
    }
  };

  // New Material Modal handlers
  const handleOpenNewMaterialModal = () => {
    setShowAddModal(true);
  };

  const handleSelectChange = (selectedOption, field) => {
    setFormData(prev => ({ ...prev, [field]: selectedOption?.value || "" }));

    if (field === 'mainCategory') {
      getSubCategories(selectedOption?.value);
      setFormData(prev => ({ ...prev, subCategory: "" }));
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      mainCategory: "",
      subCategory: "",
      name: "",
      reOrderLevel: "",
      description: "",
      unit: "",
      hsn: "",
      taxPercent: "",
      purchaseUnit: ""
    });
  };

  // Custom styles for react-select to make it more compact
  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '32px',
      height: '32px',
    }),
    valueContainer: (base) => ({
      ...base,
      height: '32px',
      padding: '0 6px',
    }),
    input: (base) => ({
      ...base,
      margin: '0px',
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: '32px',
    }),
  };

  const getDraftedProcurements = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get("procurement/draft", { headers });

      if (response?.data?.status === true) {
        const procurements = response?.data?.data?.procurements || [];
        const procurementItems = response?.data?.data?.procurementItems || [];

        // ✅ Group items by procurementId
        const itemsByProcId = procurementItems.reduce((acc, it) => {
          const pid = it.procurement; // should be procurement _id
          if (!acc[pid]) acc[pid] = [];
          acc[pid].push(it);
          return acc;
        }, {});

        const formattedDrafts = procurements.map((p) => {
          const items = itemsByProcId[p._id] || [];

          const date = new Date(p.updatedAt);
          const formattedDate = date.toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });

          return {
            ...p,
            items,                 // ✅ IMPORTANT
            itemCount: items.length,
            formattedUpdatedAt: formattedDate,
          };
        });

        setDraftedProcurements(formattedDrafts);
      }
    } catch (error) {
      // handleApiError(error, "Failed to fetch drafted procurements");
    }
  };

  const handleOpenDraft = (draft) => {
    if (!draft?._id) return;

    const mappedItems =
      (draft.items || []).map((it) => {
        const materialId = it.material?._id || it.material || "";
        const matchedMaterial = rawMaterialList.find((rm) => rm._id === materialId);

        const quantity = Number(it.quantity) || 0;
        const rate = Number(it.rate) || 0;
        const unit = it.unit || matchedMaterial?.unit || "";
        const taxPercent = Number(it.taxPercent ?? matchedMaterial?.taxPercent ?? 0);

        let actualRate = Number(it.actualRate);

        // If backend does not return actualRate, derive it from saved values
        if (!actualRate && rate > 0 && quantity > 0) {
          if (unit === "GRAMS" || unit === "ML") {
            actualRate = Number((rate * quantity).toFixed(5));
          } else {
            actualRate = rate;
          }
        }

        return {
          _id: it._id || "",
          material: materialId,
          quantity: it.quantity || "",
          rate: it.rate || "",
          taxPercent,
          unit,
          actualRate: actualRate || "",
          taxable: Number(it.taxable) || 0,
          taxAmount: Number(it.taxAmount) || 0,
          grandTotal: Number(it.grandTotal) || 0,
        };
      }) || [];

    const finalItems =
      mappedItems.length > 0
        ? mappedItems
        : [
          {
            _id: "",
            material: "",
            quantity: "",
            rate: "",
            taxPercent: "",
            unit: "",
            actualRate: "",
            taxable: 0,
            taxAmount: 0,
            grandTotal: 0,
          },
        ];

    setEditingDraftId(draft._id);

    setInputData({
      vendor: draft.vendor?._id || draft.vendor || "",
      idNumber: draft.idNumber || "",
      // additionalCharges: draft.additionalCharges ?? "",
      additionalCharges: Number(draft.additionalCharges) === 0 ? "" : (draft.additionalCharges || ""),
      procurementItems: finalItems,
    });

    setValidationErrors({
      vendor: "",
      idNumber: "",
      additionalCharges: "",
      invoiceFile: "",
      procurementItems: finalItems.map(() => ({
        material: "",
        quantity: "",
        rate: "",
      })),
    });

    setInvoiceFile(null);

    setTimeout(() => {
      if (!draft.vendor) vendorRef.current?.focus();
      else if (!draft.idNumber) idNumberRef.current?.focus();
      else if (!draft.additionalCharges) additionalChargesRef.current?.focus();
      else focusOnItem(0, "material");
    }, 150);
  };

  const getEmptyProcurementItem = () => ({
    _id: "",
    material: "",
    quantity: "",
    rate: "",
    taxPercent: "",
    unit: "",
    actualRate: "",
    taxable: 0,
    taxAmount: 0,
    grandTotal: 0,
  });

  const resetPurchaseForm = () => {
    setInputData({
      vendor: "",
      idNumber: "",
      additionalCharges: "",
      procurementItems: [getEmptyProcurementItem()],
    });

    setValidationErrors({
      vendor: "",
      idNumber: "",
      additionalCharges: "",
      invoiceFile: "",
      procurementItems: [{ material: "", quantity: "", rate: "" }],
    });

    setInvoiceFile(null);
    setEditingDraftId(null);
  };

  const handleDraftSubmit = async (e) => {
    e?.preventDefault();

    // Draft validation: vendor is mandatory
    if (!inputData.vendor || !String(inputData.vendor).trim()) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        vendor: "Vendor is required for draft.",
      }));
      vendorRef.current?.focus?.();
      return;
    }

    // Draft validation: invoice number is mandatory
    if (!inputData.idNumber || !String(inputData.idNumber).trim()) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        idNumber: "Invoice Number is required.",
      }));
      idNumberRef.current?.focus();
      return;
    }

    // Draft validation: at least one material must be selected
    const hasAtLeastOneMaterial = inputData.procurementItems.some(
      (item) => item.material && String(item.material).trim()
    );

    if (!hasAtLeastOneMaterial) {
      const procurementItemErrors = inputData.procurementItems.map((item, index) => ({
        material:
          index === 0 ? "At least one raw material must be selected for draft." : "",
        quantity: "",
        rate: "",
      }));

      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        procurementItems: procurementItemErrors,
      }));

      focusOnItem(0, "material");
      return;
    }

    setIsPurchaseDrafting(true);

    try {
      const draftItems = inputData.procurementItems.filter(
        (i) => i.material || i.quantity || i.actualRate || i.rate
      );

      const headers = {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${accessToken}`,
      };

      // CREATE DRAFT
      if (!editingDraftId) {
        const fd = new FormData();

        fd.append("procurement[vendor]", inputData.vendor || "");
        fd.append("procurement[idNumber]", inputData.idNumber || "");
        fd.append("procurement[additionalCharges]", inputData.additionalCharges || 0);

        if (invoiceFile) {
          fd.append("procurement.scannedInvoice", invoiceFile);
        }

        draftItems.forEach((item, index) => {
          if (item._id) {
            fd.append(`procurementItems[${index}][_id]`, item._id);
          }

          fd.append(`procurementItems[${index}][material]`, item.material || "");
          fd.append(`procurementItems[${index}][quantity]`, item.quantity || 0);
          fd.append(`procurementItems[${index}][rate]`, item.rate || 0);
          fd.append(`procurementItems[${index}][actualRate]`, item.actualRate || 0);
          fd.append(`procurementItems[${index}][taxPercent]`, item.taxPercent || 0);
          fd.append(`procurementItems[${index}][unit]`, item.unit || "");
          fd.append(`procurementItems[${index}][taxable]`, item.taxable || 0);
          fd.append(`procurementItems[${index}][taxAmount]`, item.taxAmount || 0);
          fd.append(`procurementItems[${index}][grandTotal]`, item.grandTotal || 0);
        });

        const response = await axios.post("procurement/draft", fd, { headers });

        if (response?.data?.status === true) {
          toast.success(response?.data?.data?.message || "Draft saved successfully", {
            autoClose: 3000,
          });

          await getDraftedProcurements();
          resetPurchaseForm();
        }

        return;
      }

      // UPDATE DRAFT
      if (draftItems.length === 0) {
        toast.warning("Please add at least one item to update the draft");
        return;
      }

      for (const item of draftItems) {
        const fd = new FormData();

        fd.append("procurement[_id]", editingDraftId);
        fd.append("procurement[vendor]", inputData.vendor || "");
        fd.append("procurement[idNumber]", inputData.idNumber || "");
        fd.append("procurement[additionalCharges]", inputData.additionalCharges || 0);

        if (invoiceFile) {
          fd.append("procurement.scannedInvoice", invoiceFile);
        }

        if (item._id) {
          fd.append("procurementItem[_id]", item._id);
        }

        fd.append("procurementItem[material]", item.material || "");
        fd.append("procurementItem[quantity]", item.quantity || 0);
        fd.append("procurementItem[rate]", item.rate || 0);
        fd.append("procurementItem[actualRate]", item.actualRate || 0);
        fd.append("procurementItem[taxPercent]", item.taxPercent || 0);
        fd.append("procurementItem[unit]", item.unit || "");
        fd.append("procurementItem[taxable]", item.taxable || 0);
        fd.append("procurementItem[taxAmount]", item.taxAmount || 0);
        fd.append("procurementItem[grandTotal]", item.grandTotal || 0);

        await axios.put("procurement/draft", fd, { headers });
      }

      toast.success("Draft updated successfully", { autoClose: 3000 });

      await getDraftedProcurements();
      resetPurchaseForm();
    } catch (error) {
      handleApiError(
        error,
        editingDraftId ? "Failed to update draft" : "Failed to save draft"
      );
    } finally {
      setIsPurchaseDrafting(false);
    }
  };

  const deleteDraftedProcurement = async (draft) => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };

      const draftProcurementId = draft?._id;
      const draftProcurementItemIds = (draft?.items || []).map((it) => it?._id);

      const response = await axios.delete("procurement/draft", {
        headers,
        data: {
          draftProcurementId,
          draftProcurementItemIds,
        },
      });

      if (response?.data?.status === true) {
        setDraftedProcurements((prev) => prev.filter((p) => p._id !== draftProcurementId));
        toast.success("Draft removed successfully");
        resetPurchaseForm();
      }
    } catch (error) {
      handleApiError(error, "Failed to delete drafted procurements");
    }
  };

  useEffect(() => {
    getDraftedProcurements();
  }, []);

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-3">
        <div className="container shadow-sm p-3" style={{ backgroundColor: "white" }}>

          {/* Header with smaller font */}
          <Row className="align-items-center mb-3">
            <Col>
              <div>
                <h6 className="fw-semibold mb-0" style={{ fontSize: '1.20rem' }}>ADD NEW PURCHASE</h6>
              </div>
            </Col>
            <Col className="text-end">
              <Button variant="outline-secondary" onClick={() => navigate("/purchase/purchase-list")}>
                Back to Purchase List
              </Button>
            </Col>
            <hr className="my-2" />
          </Row>

          {!draftedProcurements.length <= 0 &&
            <Row className="align-items-center mb-5">
              <Col xs={12} className="mb-3">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <h6 className="fw-semibold mb-0" style={{ fontSize: "1rem" }}>
                    Drafted Procurements
                  </h6>

                  <span style={{ fontSize: 12, color: "#6c757d" }}>
                    {draftedProcurements.length} draft{draftedProcurements.length > 1 ? "s" : ""}
                  </span>
                </div>
              </Col>

              <Col>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                  {draftedProcurements.map((d) => (
                    <div
                      key={d.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenDraft(d)}
                      onKeyDown={(e) => e.key === "Enter" && handleOpenDraft(d)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "12px 14px",
                        borderRadius: 14,
                        border: "1px solid #d0e6ff",
                        background: "#f2f8ff",
                        boxShadow: "0 3px 12px rgba(13,110,253,0.08)",
                        cursor: "pointer",
                        minWidth: 260,
                        maxWidth: 380,
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.background = "#e7f3ff";
                        e.currentTarget.style.borderColor = "#9ecbff";
                        e.currentTarget.style.boxShadow = "0 8px 20px rgba(13,110,253,0.15)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0px)";
                        e.currentTarget.style.background = "#f2f8ff";
                        e.currentTarget.style.borderColor = "#d0e6ff";
                        e.currentTarget.style.boxShadow = "0 3px 12px rgba(13,110,253,0.08)";
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#0b3d91",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: 220,
                          }}
                          title={d.title}
                        >
                          {d?.vendor?.name}
                        </div>

                        <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                          <span
                            style={{
                              fontSize: 12,
                              color: "#4a6fa5",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {d.itemCount} item{d.itemCount > 1 ? "s" : ""}
                          </span>

                          <span
                            style={{
                              fontSize: 12,
                              color: "#4a6fa5",
                              whiteSpace: "nowrap",
                            }}
                          >
                            • {d.formattedUpdatedAt}
                          </span>
                        </div>
                      </div>

                      <div
                        style={{
                          marginLeft: "auto",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <Button
                          variant="link"
                          title="Remove draft"
                          aria-label={`Remove ${d?.vendor?.name || "draft"}`}
                          style={{
                            padding: 0,
                            margin: 0,
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#6c757d",
                            textDecoration: "none",
                            transition: "all 0.2s ease",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteDraftedProcurement(d);
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "rgba(220,53,69,0.15)";
                            e.currentTarget.style.color = "#dc3545";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                            e.currentTarget.style.color = "#6c757d";
                          }}
                        >
                          ×
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          }

          <Form onSubmit={handlePurchaseSubmit}>
            {/* Compact form fields with smaller labels */}
            <Row className="g-3 mb-3">
              <Col md={6} lg={3}>
                <Form.Group controlId="vendorSelect">
                  <Form.Label className="mb-1" style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                    Vendor <span className="text-danger">*</span>
                  </Form.Label>
                  <Select
                    ref={vendorRef}
                    options={vendors.map((vendor) => ({
                      value: vendor._id,
                      label: vendor.name,
                    }))}
                    styles={selectStyles}
                    value={
                      inputData.vendor
                        ? {
                          value: inputData.vendor,
                          label: vendors.find(
                            (vendor) => vendor._id === inputData.vendor,
                          )?.name,
                        }
                        : null
                    }
                    onChange={(selectedOption) => {
                      handlePurchaseChange({
                        target: { name: "vendor", value: selectedOption?.value || "" },
                      });
                      setTimeout(() => idNumberRef.current?.focus(), 100);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && inputData.vendor) {
                        e.preventDefault();
                        idNumberRef.current?.focus();
                      }
                    }}
                    classNamePrefix="react-select"
                    isClearable
                    placeholder="Select Vendor"
                    autoFocus
                  />
                  {validationErrors.vendor && (
                    <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>{validationErrors.vendor}</Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col md={6} lg={3}>
                <Form.Group controlId="idNumber">
                  <Form.Label className="mb-1" style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                    Invoice Number <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    ref={idNumberRef}
                    type="text"
                    placeholder="Enter Invoice Number"
                    name="idNumber"
                    value={inputData?.idNumber}
                    onChange={handlePurchaseChange}
                    size="sm"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && inputData.idNumber) {
                        e.preventDefault();
                        additionalChargesRef.current?.focus();
                      }
                    }}
                  />
                  {validationErrors.idNumber && (
                    <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>{validationErrors.idNumber}</Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col md={6} lg={3}>
                <Form.Group controlId="additionalCharges">
                  <Form.Label className="mb-1" style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                    Additional Charges <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    ref={additionalChargesRef}
                    type="number"
                    step="any"
                    placeholder="0.00"
                    name="additionalCharges"
                    value={inputData.additionalCharges}
                    onChange={handlePurchaseChange}
                    size="sm"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && inputData.additionalCharges) {
                        e.preventDefault();
                        fileInputRef.current?.click();
                      }
                    }}
                  />
                  {validationErrors.additionalCharges && (
                    <Form.Text className="text-danger" style={{ fontSize: '0.75rem' }}>{validationErrors.additionalCharges}</Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col md={6} lg={3}>
                <Form.Group controlId="invoiceFile">
                  <Form.Label className="mb-1" style={{ fontSize: '0.85rem', fontWeight: '500' }}>Scanned Invoice</Form.Label>
                  <Form.Control
                    ref={fileInputRef}
                    type="file"
                    onChange={handlePurchaseFileChange}
                    accept=".txt, .png, .jpg, .jpeg, .pdf, .doc, .docx"
                    size="sm"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Compact items header */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0" style={{ fontSize: '0.95rem' }}>Procurement Items</h6>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleAddItem}
                title="Add new item"
                style={{ fontSize: '0.85rem', padding: '0.25rem 0.5rem' }}
              >
                <i className="bi bi-plus-lg me-1"></i> Add Item
              </Button>
            </div>

            {/* Compact items */}
            {inputData.procurementItems.map((item, index) => (
              <Card key={index} className={`mb-2 border ${focusedItemIndex === index ? 'border-primary' : ''}`}>
                <Card.Body className="p-3">
                  <div className="text-end">
                    <small className="text-muted" style={{ fontSize: '0.7rem' }}>Item #{index + 1}</small>
                  </div>

                  {/* First row - Material selection and basic fields */}
                  <Row className="g-3 mb-2">
                    <Col md={4}>
                      <Form.Group controlId={`material-${index}`}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <Form.Label style={{ fontSize: '0.8rem', fontWeight: '500', marginBottom: 0 }}>
                            Material<span className="text-danger">*</span>
                          </Form.Label>
                          <Button
                            variant="link"
                            size="sm"
                            onClick={handleOpenNewMaterialModal}
                            className="p-0 text-decoration-none"
                            style={{ fontSize: '0.75rem' }}
                            title="Add new material"
                          >
                            <i className="bi bi-plus-circle me-1"></i>
                            New
                          </Button>
                        </div>
                        <Select
                          inputId={`material-${index}`}
                          options={rawMaterialList.map((rm) => ({
                            value: rm._id,
                            label: rm.name,
                          }))}
                          styles={selectStyles}
                          value={
                            item?.material
                              ? {
                                value: item.material,
                                label: rawMaterialList?.find(
                                  (rm) => rm._id === item.material,
                                )?.name,
                              }
                              : null
                          }
                          onChange={async (selectedOption) => {
                            const materialId = selectedOption ? selectedOption.value : "";
                            handlePurchaseChange(
                              { target: { name: "material", value: materialId } },
                              index
                            );

                            if (materialId) {
                              try {
                                const headers = { Authorization: `Bearer ${accessToken}` };
                                const response = await axios.get(`raw-material?_id=${materialId}`, { headers });

                                if (response?.data?.status === true) {
                                  const raw = response?.data?.data?.rawMaterials;
                                  const taxPercent = raw?.taxPercent || 0;
                                  const unit = raw?.unit || "";

                                  setInputData((prevData) => {
                                    const updatedItems = [...prevData.procurementItems];
                                    updatedItems[index] = {
                                      ...updatedItems[index],
                                      taxPercent,
                                      unit,
                                    };
                                    return { ...prevData, procurementItems: updatedItems };
                                  });

                                  setTimeout(() => focusOnItem(index, 'quantity'), 100);
                                }
                              } catch (error) {
                                console.error("Error fetching raw material details:", error);
                                toast.error("Failed to fetch raw material details");
                              }
                            } else {
                              setInputData((prevData) => {
                                const updatedItems = [...prevData.procurementItems];
                                updatedItems[index] = {
                                  ...updatedItems[index],
                                  taxPercent: 0,
                                  unit: "",
                                };
                                return { ...prevData, procurementItems: updatedItems };
                              });
                            }
                          }}
                          onKeyDown={(e) => handleKeyDown(e, index, 'material')}
                          classNamePrefix="react-select"
                          isClearable
                          placeholder="Select"
                          autoFocus={index === focusedItemIndex}
                        />
                        {validationErrors.procurementItems[index]?.material && (
                          <Form.Text className="text-danger" style={{ fontSize: '0.7rem' }}>
                            {validationErrors.procurementItems[index]?.material}
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>

                    <Col md={2}>
                      <Form.Group controlId={`quantity-${index}`}>
                        <Form.Label style={{ fontSize: '0.8rem', fontWeight: '500', marginBottom: '2px' }}>
                          Qty<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          id={`quantity-${index}`}
                          step="any"
                          type="number"
                          placeholder="0"
                          name="quantity"
                          value={item.quantity}
                          onChange={(e) => handlePurchaseChange(e, index)}
                          onKeyDown={(e) => handleKeyDown(e, index, 'quantity')}
                          onFocus={() => setFocusedItemIndex(index)}
                          size="sm"
                        />
                        {validationErrors.procurementItems[index]?.quantity && (
                          <Form.Text className="text-danger" style={{ fontSize: '0.7rem' }}>
                            {validationErrors.procurementItems[index]?.quantity}
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>

                    <Col md={1}>
                      <Form.Group controlId={`unit-${index}`}>
                        <Form.Label style={{ fontSize: '0.8rem', fontWeight: '500', marginBottom: '2px' }}>Unit</Form.Label>
                        <Form.Control
                          disabled
                          type="text"
                          value={inputData.procurementItems[index]?.unit || ""}
                          size="sm"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={2}>
                      <Form.Group controlId={`actualRate-${index}`}>
                        <Form.Label style={{ fontSize: '0.8rem', fontWeight: '500', marginBottom: '2px' }}>
                          Act.Rate<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          id={`actualRate-${index}`}
                          step="any"
                          type="number"
                          placeholder="0.00"
                          name="actualRate"
                          value={item.actualRate || ""}
                          onChange={(e) => handleRateChange(e, index)}
                          onKeyDown={(e) => handleKeyDown(e, index, 'actualRate')}
                          onFocus={() => setFocusedItemIndex(index)}
                          size="sm"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={2}>
                      <Form.Group controlId={`rate-${index}`}>
                        <Form.Label style={{ fontSize: '0.8rem', fontWeight: '500', marginBottom: '2px' }}>
                          Rate<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          id={`rate-${index}`}
                          step="any"
                          type="number"
                          placeholder="0.00"
                          name="rate"
                          value={item.rate}
                          onChange={(e) => handlePurchaseChange(e, index)}
                          onKeyDown={(e) => handleKeyDown(e, index, 'rate')}
                          onFocus={() => setFocusedItemIndex(index)}
                          size="sm"
                        />
                        {validationErrors.procurementItems[index]?.rate && (
                          <Form.Text className="text-danger" style={{ fontSize: '0.7rem' }}>
                            {validationErrors.procurementItems[index]?.rate}
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>

                    <Col md={1} className="d-flex align-items-end">
                      <div className="d-flex">
                        {inputData.procurementItems.length > 1 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveItem(index)}
                            className="ms-2"
                            style={{ padding: '0.2rem 0.4rem', fontSize: '0.75rem' }}
                            title="Remove item"
                          >
                            <i className="bi bi-dash-lg"></i>
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Row>

                  {/* Second row - Tax calculations */}
                  <Row className="g-3">
                    <Col md={3}>
                      <Form.Group controlId={`taxPercent-${index}`}>
                        <Form.Label style={{ fontSize: '0.75rem', fontWeight: '500', marginBottom: '2px' }}>Tax %</Form.Label>
                        <Form.Control
                          disabled
                          type="text"
                          value={`${inputData.procurementItems[index].taxPercent || "0"}%`}
                          size="sm"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group controlId={`taxable-${index}`}>
                        <Form.Label style={{ fontSize: '0.75rem', fontWeight: '500', marginBottom: '2px' }}>Taxable</Form.Label>
                        <Form.Control
                          disabled
                          type="text"
                          value={`₹${parseFloat(inputData.procurementItems[index].taxable || "0").toFixed(2)}`}
                          size="sm"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group controlId={`taxAmount-${index}`}>
                        <Form.Label style={{ fontSize: '0.75rem', fontWeight: '500', marginBottom: '2px' }}>Tax Amt</Form.Label>
                        <Form.Control
                          disabled
                          type="text"
                          value={`₹${parseFloat(inputData.procurementItems[index].taxAmount || "0").toFixed(2)}`}
                          size="sm"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={3}>
                      <Form.Group controlId={`grandTotal-${index}`}>
                        <Form.Label style={{ fontSize: '0.75rem', fontWeight: '500', marginBottom: '2px' }}>Total</Form.Label>
                        <Form.Control
                          disabled
                          type="text"
                          value={`₹${parseFloat(inputData.procurementItems[index].grandTotal || "0").toFixed(2)}`}
                          size="sm"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ))}

            {/* Compact submit button */}
            <div className="d-flex justify-content-end mt-3">
              <Button
                onClick={handleDraftSubmit}
                variant="success"
                type="button"
                disabled={isPurchaseDrafting}
                className="px-3"
                size="sm"
                style={{ fontSize: '0.85rem', marginRight: '10px', color: '#fff' }}
                title="Submit purchase"
              >
                {isPurchaseLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    <span className="ms-2">Processing...</span>
                  </>
                ) : (
                  "Save Draft"
                )}
              </Button>
              <Button
                ref={submitButtonRef}
                variant="primary"
                type="submit"
                disabled={purchaseBtnDisable() || isPurchaseLoading}
                className="px-3"
                size="sm"
                style={{ fontSize: '0.85rem' }}
                title="Submit purchase"
              >
                {isPurchaseLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    <span className="ms-2">Processing...</span>
                  </>
                ) : (
                  "Submit Purchase"
                )}
              </Button>
            </div>
          </Form>

          {/* Add Material Modal - Compact version */}
          {showAddModal && (
            <Modal
              show={showAddModal}
              onHide={() => {
                setShowAddModal(false);
                resetForm();
              }}
              size="lg"
              backdrop="static"
            >
              <Modal.Header closeButton className="py-2">
                <Modal.Title style={{ fontSize: '1rem' }}>ADD NEW RAW MATERIAL</Modal.Title>
              </Modal.Header>
              <Form onSubmit={addMaterial}>
                <Modal.Body className="py-2">
                  <Row className="mb-2">
                    <Col md={6}>
                      <Form.Group controlId="formMainCategory" className="mb-2">
                        <Form.Label style={{ fontSize: '0.85rem', fontWeight: '500', marginBottom: '2px' }}>
                          Main Category <span className="text-danger">*</span>
                        </Form.Label>
                        <Select
                          options={mainCategoryOptions}
                          styles={selectStyles}
                          value={mainCategoryOptions.find(opt => opt.value === formData.mainCategory)}
                          onChange={(selected) => handleSelectChange(selected, 'mainCategory')}
                          isClearable
                          isSearchable
                          placeholder="Select main category"
                          required
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && formData.mainCategory) {
                              e.preventDefault();
                              document.querySelector('#formSubCategory input')?.focus();
                            }
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="formSubCategory" className="mb-2">
                        <Form.Label style={{ fontSize: '0.85rem', fontWeight: '500', marginBottom: '2px' }}>
                          Sub Category
                        </Form.Label>
                        <Select
                          options={subCategoryOptions}
                          styles={selectStyles}
                          value={subCategoryOptions.find(opt => opt.value === formData.subCategory)}
                          onChange={(selected) => handleSelectChange(selected, 'subCategory')}
                          isClearable
                          isSearchable
                          placeholder="Select sub category"
                          isDisabled={!formData.mainCategory}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              document.querySelector('#formName')?.focus();
                            }
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-2">
                    <Col md={6}>
                      <Form.Group controlId="formName" className="mb-2">
                        <Form.Label style={{ fontSize: '0.85rem', fontWeight: '500', marginBottom: '2px' }}>
                          Name <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleFormChange}
                          placeholder="Enter material name"
                          required
                          size="sm"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && formData.name) {
                              e.preventDefault();
                              document.querySelector('#formUnit')?.focus();
                            }
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="formUnit" className="mb-2">
                        <Form.Label style={{ fontSize: '0.85rem', fontWeight: '500', marginBottom: '2px' }}>
                          Unit <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          name="unit"
                          value={formData.unit}
                          onChange={handleFormChange}
                          required
                          size="sm"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && formData.unit) {
                              e.preventDefault();
                              document.querySelector('#formHSN')?.focus();
                            }
                          }}
                        >
                          <option value="">Select unit</option>
                          <option value="LITRES">LITRES</option>
                          <option value="ML">ML</option>
                          <option value="GRAMS">GRAMS</option>
                          <option value="KILOGRAMS">KILOGRAMS</option>
                          <option value="PACKETS">PACKETS</option>
                          <option value="PCS">PCS</option>
                          <option value="BOX">BOX</option>
                          <option value="NOS">NOS</option>
                          <option value="MM">MM</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-2">
                    <Col md={6}>
                      <Form.Group controlId="formHSN" className="mb-2">
                        <Form.Label style={{ fontSize: '0.85rem', fontWeight: '500', marginBottom: '2px' }}>
                          HSN Code <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="hsn"
                          value={formData.hsn}
                          onChange={handleFormChange}
                          placeholder="Enter HSN code"
                          required
                          size="sm"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && formData.hsn) {
                              e.preventDefault();
                              document.querySelector('#formTax')?.focus();
                            }
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="formTax" className="mb-2">
                        <Form.Label style={{ fontSize: '0.85rem', fontWeight: '500', marginBottom: '2px' }}>
                          Tax % <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="taxPercent"
                          value={formData.taxPercent}
                          onChange={handleFormChange}
                          min="0"
                          max="100"
                          placeholder="Enter tax %"
                          required
                          size="sm"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && formData.taxPercent) {
                              e.preventDefault();
                              document.querySelector('#formPurchaseUnit')?.focus();
                            }
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-2">
                    <Col md={6}>
                      <Form.Group controlId="formPurchaseUnit" className="mb-2">
                        <Form.Label style={{ fontSize: '0.85rem', fontWeight: '500', marginBottom: '2px' }}>
                          Purchase Unit <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          name="purchaseUnit"
                          value={formData.purchaseUnit}
                          onChange={handleFormChange}
                          required
                          size="sm"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && formData.purchaseUnit) {
                              e.preventDefault();
                              document.querySelector('#formReorder')?.focus();
                            }
                          }}
                        >
                          <option value="">Select purchase unit</option>
                          <option value="FOOD">Food</option>
                          <option value="BEVERAGE">Beverage</option>
                          <option value="CENTRAL">Central</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="formReorder" className="mb-2">
                        <Form.Label style={{ fontSize: '0.85rem', fontWeight: '500', marginBottom: '2px' }}>
                          Re-Order Level
                        </Form.Label>
                        <Form.Control
                          type="number"
                          name="reOrderLevel"
                          value={formData.reOrderLevel}
                          onChange={handleFormChange}
                          min="0"
                          placeholder="Enter re-order level"
                          size="sm"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              document.querySelector('#formDescription')?.focus();
                            }
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <Form.Group controlId="formDescription" className="mb-2">
                        <Form.Label style={{ fontSize: '0.85rem', fontWeight: '500', marginBottom: '2px' }}>
                          Description <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          name="description"
                          value={formData.description}
                          onChange={handleFormChange}
                          placeholder="Enter description"
                          required
                          size="sm"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              document.querySelector('button[type="submit"]')?.focus();
                            }
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Modal.Body>
                <Modal.Footer className="py-2">
                  <Button variant="secondary" size="sm" onClick={() => setShowAddModal(false)} style={{ fontSize: '0.85rem' }}>
                    Cancel
                  </Button>
                  <Button variant="warning" type="submit" disabled={isLoading} size="sm" style={{ fontSize: '0.85rem' }}>
                    {isLoading ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        <span className="ms-2">Adding...</span>
                      </>
                    ) : (
                      "Add Material"
                    )}
                  </Button>
                </Modal.Footer>
              </Form>
            </Modal>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default AddPurchase;